
import { Component, Vue } from "vue-property-decorator";
import {
  QUERY_TIPS_WITH_STATS,
  MUTATION_SET_PIN_ORDER,
  QUERY_CITIES
} from "./graphql/tipOrder";

interface TipWithStats {
  id: string;
  title: string;
  city: string | null;
  country: string | null;
  authorUsername: string;
  pinOrder: number | null;
  nbImpressions: number;
  nbClicksDetail: number;
  nbClicksStay22: number;
  score: number | null;
  scoreCity: number | null;
}

interface CityOption {
  label: string;
  context: string;
}

@Component
export default class TipOrder extends Vue {
  loading = false;
  tips: TipWithStats[] = [];
  search = "";
  selectedCity: string | null = null;
  platform = "app";
  cityOptions: CityOption[] = [];
  platformOptions = ["app", "web"];

  pinDialog = false;
  pinTarget: TipWithStats | null = null;
  pinValue: number | null = null;

  get context(): string {
    return this.selectedCity || "global";
  }

  get selectedCityLabel(): string {
    if (!this.selectedCity) return "";
    const found = this.cityOptions.find(c => c.context === this.selectedCity);
    return found ? found.label : this.selectedCity;
  }

  get computedHeaders() {
    const base = [
      { text: "Tier", value: "tier", sortable: false, width: "100px" },
      { text: "Score feed", value: "score", width: "110px" },
      { text: "Score ville", value: "scoreCity", width: "110px" },
      { text: this.selectedCity ? "Pin Ville" : "Pin Feed", value: "pinOrder", width: "110px" },
      { text: "Titre", value: "title" },
      { text: "Localisation", value: "location", sortable: false },
      { text: "Auteur", value: "authorUsername", width: "150px" },
      { text: "Affichages", value: "nbImpressions", width: "110px" },
      { text: "Clics détail", value: "nbClicksDetail", width: "110px" },
      { text: "Clics Stay22", value: "nbClicksStay22", width: "130px" }
    ];
    return base;
  }

  tierLabel(tip: TipWithStats): string {
    const imp = this.selectedCity
      ? tip.scoreCity != null
        ? tip.nbImpressions
        : 0
      : tip.nbImpressions;
    if (imp >= 500) return "Établi";
    if (imp >= 50) return "Émergent";
    return "Nouveau";
  }

  tierColor(tip: TipWithStats): string {
    const label = this.tierLabel(tip);
    if (label === "Établi") return "green";
    if (label === "Émergent") return "orange";
    return "grey";
  }

  scoreColor(score: number): string {
    if (score >= 0.05) return "green";
    if (score >= 0.01) return "orange";
    return "red";
  }

  get filteredTips(): TipWithStats[] {
    if (!this.search) return this.tips;
    const q = this.search.toLowerCase();
    return this.tips.filter(
      t =>
        t.title.toLowerCase().includes(q) ||
        (t.city || "").toLowerCase().includes(q) ||
        (t.country || "").toLowerCase().includes(q) ||
        (t.authorUsername || "").toLowerCase().includes(q)
    );
  }

  async mounted() {
    await this.fetchCities();
    await this.fetchTips();
  }

  async fetchCities() {
    try {
      const { data } = await this.$apollo.query({
        query: QUERY_CITIES,
        fetchPolicy: "no-cache"
      });
      this.cityOptions = (data.cities || [])
        .filter((c: any) => c.city)
        .map((c: any) => ({
          label: c.country ? `${c.city}, ${c.country}` : c.city,
          context: `city:${c.id}`
        }))
        .sort((a: CityOption, b: CityOption) => a.label.localeCompare(b.label));
    } catch {
      // not critical
    }
  }

  async fetchTips() {
    this.loading = true;
    try {
      if (this.selectedCity) {
        // City view: fetch city context (for city score + city pins) AND global context (for feed score)
        const [cityResult, globalResult] = await Promise.all([
          this.$apollo.query({
            query: QUERY_TIPS_WITH_STATS,
            variables: { context: this.selectedCity, platform: this.platform, limit: 200 },
            fetchPolicy: "no-cache"
          }),
          this.$apollo.query({
            query: QUERY_TIPS_WITH_STATS,
            variables: { context: "global", platform: this.platform, limit: 200 },
            fetchPolicy: "no-cache"
          })
        ]);
        const globalMap = new Map<string, number | null>(
          globalResult.data.tipsWithStats.map((t: any) => [t.id, t.score])
        );
        this.tips = cityResult.data.tipsWithStats.map((t: any) => ({
          ...t,
          score: globalMap.get(t.id) !== undefined ? globalMap.get(t.id) : null,   // feed score (from global context)
          scoreCity: t.score                     // city score (from city context)
        }));
      } else {
        // Global view: backend returns both score (feed) and scoreCity (native city) directly
        const result = await this.$apollo.query({
          query: QUERY_TIPS_WITH_STATS,
          variables: { context: "global", platform: this.platform, limit: 200 },
          fetchPolicy: "no-cache"
        });
        this.tips = result.data.tipsWithStats;
      }
    } catch (err) {
      this.$store.commit("setSnackMessageWithColor", {
        message: String(err),
        color: "error"
      });
    } finally {
      this.loading = false;
    }
  }

  openPinDialog(tip: TipWithStats) {
    this.pinTarget = tip;
    this.pinValue = tip.pinOrder || null;
    this.pinDialog = true;
  }

  async confirmPin(pinOrder: number | null) {
    this.pinDialog = false;
    if (!this.pinTarget) return;
    await this.setPinOrder(this.pinTarget, pinOrder);
    this.pinTarget = null;
  }

  customSort(items: TipWithStats[], sortBy: string[], sortDesc: boolean[]): TipWithStats[] {
    if (sortBy.length === 0) return items;
    const key = sortBy[0];
    const desc = sortDesc[0];

    return items.slice().sort((a, b) => {
      if (key === 'pinOrder') {
        // Toujours ascendant (#1, #2...), non-pinnés à la fin
        if (a.pinOrder == null && b.pinOrder == null) return 0;
        if (a.pinOrder == null) return 1;
        if (b.pinOrder == null) return -1;
        return a.pinOrder - b.pinOrder;
      }
      const va = (a as any)[key];
      const vb = (b as any)[key];
      if (va == null && vb == null) return 0;
      if (va == null) return desc ? -1 : 1;
      if (vb == null) return desc ? 1 : -1;
      const cmp = va < vb ? -1 : va > vb ? 1 : 0;
      return desc ? -cmp : cmp;
    });
  }

  async setPinOrder(tip: TipWithStats, pinOrder: number | null) {
    try {
      await this.$apollo.mutate({
        mutation: MUTATION_SET_PIN_ORDER,
        variables: { tipId: tip.id, pinOrder, context: this.context }
      });
      this.$store.commit("setSnackMessageWithColor", {
        message:
          pinOrder != null ? `Pin #${pinOrder} enregistré` : "Pin retiré",
        color: "success"
      });
      await this.fetchTips();
    } catch (err) {
      this.$store.commit("setSnackMessageWithColor", {
        message: String(err),
        color: "error"
      });
    }
  }
}
